import { graphql, Link, Script } from "gatsby"
import React, { useEffect } from "react"
import renderMathInElement from "../utils/katex-auto-render.js"

import "highlight.js/styles/github.css"
import ArticleFooterBottom from "../components/article/ArticleFooterBottom"
import Layout from "../components/Layout"
import MetaData from "../components/meta"
import ReadingProgress from "../components/ReadingProgress"
import RenderContent from "../components/RenderContent"
import ResourceCard from "../components/ResourceCard"
import "../styles/blog-post.scss"

// Images
import StickyMenu from "../components/article/StickyMenu"
import ArrowBlue from "../images/inline-svgs/right-arrow-blue.svg"

const Post = ({ data, location }) => {
  const post = data.ghostPost
  const author = post.primary_author
  let latestBlogs = data.latest_blogs.edges
  let latestResources = data.latest_resources.edges

  // Remove the current post from the latest blogs
  latestBlogs = latestBlogs.filter(blog => blog.node.id !== post.id)
  if (latestBlogs.length > 2) {
    latestBlogs.pop()
  }
  latestBlogs = latestBlogs.concat(latestResources)

  useEffect(() => {
    if (window?.location?.hostname === "traefik.io") {
      // Add discourse comments
      window.DiscourseEmbed = {
        discourseUrl: "https://community.traefik.io/",
        discourseEmbedUrl: `https://${window.location.host}/${post.primary_tag.slug}/${post.slug}/`,
        discourseReferrerPolicy: "no-referrer-when-downgrade",
      }
      var d = document.createElement("script")
      d.type = "text/javascript"
      d.async = true
      d.src = `${DiscourseEmbed.discourseUrl}javascripts/embed.js`
      ;(
        document.getElementsByTagName("head")[0] ||
        document.getElementsByTagName("body")[0]
      ).appendChild(d)
    }

    renderMathInElement(document.body)
  }, [])

  // Reading progress bar
  const target: React.RefObject<HTMLElement> = React.createRef()

  const htmlAst = post.childHtmlRehype && post.childHtmlRehype.htmlAst
  const transformedHtml = post.childHtmlRehype && post.childHtmlRehype.html

  return (
    <Layout>
      <MetaData data={data} location={location} type="article" />
      <ReadingProgress target={target} />

      <Script
        src="https://js.hubspot.com/web-interactives-embed.js"
        data-hsjs-portal="5814022"
        data-hsjs-hublet="na1"
        id="hubspot-web-interactives-loader"
        data-loader="hs-scriptloader"
        data-hsjs-env="prod"
      />

      {/* Add codeinjection scripts */}
      <div
        className="load-external-scripts"
        dangerouslySetInnerHTML={{ __html: post.codeinjection_head }}
      ></div>

      <div className="post-view">
        <div className="container--boxed">
          {/* Go back button */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="post__primary-tag">
              <Link to={`/${post.primary_tag.slug}/`} className="go-back-link">
                <span className="icon">
                  <ArrowBlue />
                </span>
                <span className="text">{post.primary_tag.name}</span>
              </Link>
            </div>
            {/* Date */}
            <div className="text-sm text-neutral-500 mt-10">
              {post.published_at_pretty}
            </div>
          </div>

          <article
            className={
              "max-w-4xl mx-auto mt-6 relative mb-12" +
              (post.feature_image == null ? " no-feature-image" : "")
            }
            ref={target}
          >
            <header>
              <h1 className="heading--h2">{post.title}</h1>

              <div className="flex flex-col  space-x-6 md:flex-row justify-start items-start md:items-center flex-wrap mt-6 mb-10">
                {/* Authors */}
                <div className="flex items-center">
                  <div className="flex">
                    {post.authors?.map((author, index) => (
                      <Link
                        to={`/author/${author.slug}/`}
                        key={author.slug}
                        className="w-8 h-8 rounded-full overflow-hidden -ml-[6px] first:ml-0 transition-all"
                      >
                        <img
                          src={author.profile_image}
                          alt={author.name}
                          style={{
                            borderRadius: "100%",
                            objectFit: "cover",
                          }}
                          loading="lazy"
                          className="w-8 h-8 object-cover object-center"
                        />
                      </Link>
                    ))}
                  </div>
                  <div className="flex items-center">
                    {post.authors?.map((author, index) => (
                      <Link
                        to={`/author/${author.slug}/`}
                        key={author.slug}
                        className="text-sm text-neutral-500 ml-1 first:ml-2 hover:underline"
                      >
                        {index >= 1 ? ", " : ""}
                        {author.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div className="flex items-center space-x-6 mt-2 md:mt-0">
                  {post.tags.map((tag, index) => {
                    if (index == 0 || tag.visibility === "internal") return
                    return (
                      <div key={tag.slug} className="flex items-center group">
                        <Link
                          to={`/tag/${tag.slug}/`}
                          className="text-neutral-700 underline hover:text-opacity-80 text-sm"
                        >
                          {tag.name}
                        </Link>
                      </div>
                    )
                  })}
                </div>
              </div>
            </header>

            {/* Post Content */}
            <div className="relative post__body">
              <RenderContent
                htmlAst={htmlAst}
                html={transformedHtml || post.html}
              />
              <StickyMenu
                custom_template={post.custom_template}
                post={post}
                html={transformedHtml || post.html}
              />
            </div>
          </article>
        </div>

        <ArticleFooterBottom author={author} html={post.html} />

        {/* Discourse comments */}
        <div className="section--lg bg-white">
          <div className="container--boxed">
            <div id="discourse-comments"></div>
          </div>
        </div>

        {/* Related Posts */}
        {latestBlogs.length > 0 && (
          <div className="section--lg bg-neutral-50">
            <div className="container--boxed">
              <h2 className="heading--h2 text-center mb-14">
                Latest from Traefik Labs
              </h2>
              <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-15 gap-x-6 justify-items-center">
                {latestBlogs.map((latestPost, index) => (
                  <ResourceCard
                    key={latestPost.node.id}
                    data={latestPost.node}
                    styles={{
                      linkColor: "text.dark",
                      eyebrowColor: "text.light",
                    }}
                    headingVariant="h3"
                    eyebrowOverrideText={index < 2 ? "Blog" : null}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <div
          className="load-external-scripts"
          dangerouslySetInnerHTML={{ __html: post.codeinjection_foot }}
        ></div>
      </div>
    </Layout>
  )
}

export default Post

export const postQuery = graphql`
  query ($slug: String!) {
    ghostPost(slug: { eq: $slug }) {
      childHtmlRehype {
        html
        htmlAst
      }

      # Main fields
      custom_template
      id
      title
      slug
      feature_image

      # Used for meta tag preview image on posts
      featureImageSharp {
        publicURL
      }

      excerpt
      custom_excerpt
      visibility

      # Dates formatted
      created_at_pretty: created_at(formatString: "DD MMMM, YYYY")
      published_at_pretty: published_at(formatString: "MMMM D, YYYY")
      updated_at_pretty: updated_at(formatString: "DD MMMM, YYYY")

      # Dates unformatted
      created_at
      published_at
      updated_at

      # SEO
      meta_title
      meta_description
      og_description
      og_image
      og_title
      twitter_description
      twitter_image
      twitterImageSharp {
        publicURL
        childImageSharp {
          resize(width: 1200) {
            src
          }
        }
      }
      twitter_title

      # Authors
      authors {
        name
        slug
        profile_image
      }
      primary_author {
        name
        slug
        bio
        profile_image
      }

      # Tags
      primary_tag {
        name
        slug
        visibility
      }
      tags {
        name
        slug
        visibility
      }

      # Content
      html

      # Additional fields
      url
      canonical_url
      uuid
      codeinjection_foot
      codeinjection_head
      codeinjection_styles
      reading_time
    }

    latest_blogs: allGhostPost(
      filter: { primary_tag: { slug: { eq: "blog" } } }
      sort: { published_at: DESC }
      limit: 3
    ) {
      edges {
        node {
          ...GhostCarouselCardFields
        }
      }
    }

    latest_resources: allGhostPost(
      filter: { primary_tag: { slug: { eq: "resources" } } }
      sort: { published_at: DESC }
      limit: 1
    ) {
      edges {
        node {
          ...GhostCarouselCardFields
        }
      }
    }
  }
`
